import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { priceVarianceAPI, type PriceVarianceResponse, type PriceVarianceRow, type PriceVarianceStatus } from '../../lib/api/priceVariance';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { ListPager, ListSearch, useListControls } from '../../components/common/listControls';
import { SortableTh } from '../../components/common/SortableTh';
import { UploadTableEmpty, UploadTableSkeleton } from '../barratt/upload-history/UploadTableStates';
import { formatDay } from '../barratt/uploadCycle';
import { varianceTextClass } from '../../components/purchasing/contractPriceVariance';
import { AlertCircle, Download, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import PriceVarianceTiles from './PriceVarianceTiles';

type StatusFilter = PriceVarianceStatus | 'all';

function monthBounds(period: string): { start: string; end: string } {
  const [y, m] = period.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { start: `${period}-01`, end: `${period}-${String(last).padStart(2, '0')}` };
}

function lastMonth(): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

const STATUS_LABEL: Record<PriceVarianceStatus, string> = { over: 'Over', exact: 'Exact', under: 'Under' };

/**
 * Price Variance — invoice lines for a month against the contract pricebook
 * rate for the same supplier and resource code. Strict rule: over, exact or
 * under, no tolerance. Lines with no live contract rate are counted, not compared.
 */
export default function PriceVariancePage() {
  const { getViewQueryParams, viewKey } = useView();
  const { formatCurrency } = useFormatters();
  const [params, setParams] = useSearchParams();
  const period = params.get('period') || lastMonth();
  const [data, setData] = useState<PriceVarianceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('over');
  const [supplier, setSupplier] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const { start, end } = monthBounds(period);
    const scope: Record<string, number | undefined> = getViewQueryParams();
    setLoading(true);
    setError(null);
    priceVarianceAPI.get({ start, end, site: scope['site'], company: scope['company'], group: scope['group'] })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Price variance load failed:', err);
        setError(getErrorMessage(err, 'Could not load price variance'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, getViewQueryParams, viewKey, tick]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const supplierOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => seen.set(r.supplier_id, r.supplier_name));
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ value: String(id), label: name }));
  }, [rows]);
  const filtered = useMemo(() => rows.filter((r) =>
    (statusFilter === 'all' || r.status === statusFilter) && (!supplier || String(r.supplier_id) === supplier)), [rows, statusFilter, supplier]);
  const counts = useMemo(() => ({
    over: rows.filter((r) => r.status === 'over').length,
    exact: rows.filter((r) => r.status === 'exact').length,
    under: rows.filter((r) => r.status === 'under').length,
    all: rows.length,
  }), [rows]);

  const list = useListControls(filtered, {
    searchText: (r) => [r.supplier_name, r.po_number, r.resource_code, r.description, r.contract_number].join(' '),
    sorters: {
      supplier: (r) => r.supplier_name.toLowerCase(),
      date: (r) => r.invoice_date,
      code: (r) => r.resource_code,
      qty: (r) => Number(r.quantity),
      unit_price: (r) => Number(r.unit_price),
      contract_price: (r) => Number(r.contract_price),
      variance_value: (r) => Number(r.variance_value),
    },
    defaultSort: { key: 'variance_value', dir: 'desc' },
    tieBreak: (r) => `${r.supplier_name}|${r.id}`,
    resetKeys: [statusFilter, supplier, period],
  });

  const money = (v: string, dp = 2) => formatCurrency(Number(v), 'GBP', dp);

  const exportExcel = () => {
    const sheet = XLSX.utils.aoa_to_sheet([
      ['Supplier', 'Invoice date', 'PO', 'Resource code', 'Description', 'Division', 'Qty', 'Unit', 'Net amount', 'Unit price', 'Contract price', 'Contract', 'Variance / unit', 'Variance value', 'Status'],
      ...list.orderedRows.map((r: PriceVarianceRow) => [
        r.supplier_name, r.invoice_date, r.po_number, r.resource_code, r.description, r.division_name, Number(r.quantity), r.unit,
        Number(r.net_amount), Number(r.unit_price), Number(r.contract_price), r.contract_number, Number(r.variance), Number(r.variance_value), STATUS_LABEL[r.status],
      ]),
    ]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Price variance');
    const parts = ['price-variance', period, statusFilter !== 'all' ? statusFilter : '', supplier].filter(Boolean);
    XLSX.writeFile(book, `${parts.join('-')}.xlsx`);
  };

  const columns = 10;
  const emptyMessage = rows.length === 0
    ? (data && data.totals.uncompared_count > 0
      ? `${data.totals.uncompared_count.toLocaleString()} invoice lines in this month, none with a live contract rate to compare against. Add pricebooks under Contracts → Pricebooks.`
      : 'No invoice lines in this month.')
    : list.search ? `No lines match "${list.search}".` : 'No lines in this view.';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Price Variance</h1>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          Each invoice line's unit price (net ÷ quantity) against the contracted rate for the same supplier and resource code.
          Over, exact or under — there is no tolerance. Lines with no live contract rate are counted but not compared.
        </p>
      </div>

      <PriceVarianceTiles totals={data?.totals ?? null} loading={loading} />

      {error ? (
        <Card><CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <AlertCircle className="h-10 w-10 text-red-500" />
          <p className="text-sm font-medium">Could not load price variance</p>
          <p className="max-w-md text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={() => setTick((t) => t + 1)}><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
        </CardContent></Card>
      ) : (
        <>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <TabsList data-testid="price-variance-status-filter">
                <TabsTrigger value="over">Over ({counts.over})</TabsTrigger>
                <TabsTrigger value="exact">Exact ({counts.exact})</TabsTrigger>
                <TabsTrigger value="under">Under ({counts.under})</TabsTrigger>
                <TabsTrigger value="all">All compared ({counts.all})</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <Label htmlFor="pv-period" className="text-sm text-muted-foreground">Month</Label>
                <Input id="pv-period" type="month" value={period} className="w-44"
                       onChange={(e) => { if (e.target.value) setParams({ period: e.target.value }); }} data-testid="price-variance-period" />
              </div>
              <div className="md:w-64">
                <SearchableSelect options={supplierOptions} value={supplier} onChange={setSupplier} placeholder="All suppliers" />
              </div>
              <div className="md:w-64">
                <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search supplier, PO, code, contract…" testId="price-variance-search" />
              </div>
              <Button variant="outline" size="sm" onClick={exportExcel} disabled={list.orderedRows.length === 0} data-testid="price-variance-export">
                <Download className="mr-2 h-4 w-4" />Export
              </Button>
            </div>
          </div>

          {data?.truncated && (
            <p className="text-sm text-muted-foreground">Showing the first {rows.length.toLocaleString()} compared lines of {data.totals.compared_count.toLocaleString()}; the tiles cover all of them. Narrow the month or export.</p>
          )}

          <Card><CardContent className="p-0">
            <ListPager list={list} position="top" noun="lines" testIdPrefix="price-variance" />
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <SortableTh field="supplier" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Supplier</SortableTh>
                  <SortableTh field="date" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Invoiced</SortableTh>
                  <SortableTh field="code" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Resource</SortableTh>
                  <TableHead>PO</TableHead>
                  <SortableTh field="qty" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">Qty</SortableTh>
                  <SortableTh field="unit_price" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">Invoice price</SortableTh>
                  <SortableTh field="contract_price" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">Contract rate</SortableTh>
                  <TableHead>Contract</TableHead>
                  <SortableTh field="variance_value" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">Variance</SortableTh>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                {loading ? <UploadTableSkeleton columns={columns} />
                  : list.pageRows.length === 0 ? <UploadTableEmpty columns={columns} message={emptyMessage} />
                  : (
                    <TableBody>
                      {list.pageRows.map((r) => (
                        <TableRow key={r.id} className={r.status === 'over' ? 'border-l-4 border-l-red-500' : ''} data-testid={`price-variance-${r.id}`}>
                          <TableCell>
                            <div className="font-medium">{r.supplier_name}</div>
                            {r.division_name && <div className="text-xs text-muted-foreground">{r.division_name}</div>}
                          </TableCell>
                          <TableCell>{formatDay(r.invoice_date)}</TableCell>
                          <TableCell>
                            <div className="font-mono text-xs">{r.resource_code}</div>
                            <div className="line-clamp-1 text-xs text-muted-foreground">{r.description}</div>
                          </TableCell>
                          <TableCell className="text-xs">{r.po_number || '—'}</TableCell>
                          <TableCell className="text-right">{Number(r.quantity).toLocaleString()} <span className="text-xs text-muted-foreground">{r.unit}</span></TableCell>
                          <TableCell className={`text-right font-semibold ${varianceTextClass(r.status)}`}>{money(r.unit_price, 4)}</TableCell>
                          <TableCell className="text-right">{money(r.contract_price, 4)}</TableCell>
                          <TableCell>
                            <Link to={`/contracts?tab=pricebooks&contract=${r.contract_id}`} className="text-primary hover:underline">{r.contract_number}</Link>
                          </TableCell>
                          <TableCell className={`text-right font-semibold ${varianceTextClass(r.status)}`}>{money(r.variance_value)}</TableCell>
                          <TableCell><span className={`text-sm font-medium ${varianceTextClass(r.status)}`}>{STATUS_LABEL[r.status]}</span></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
              </Table>
            </div>
            <ListPager list={list} position="bottom" noun="lines" testIdPrefix="price-variance" />
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
